import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Globe, Server, CheckCircle2, AlertCircle, ExternalLink, Copy, Check,
  Loader2, Save, Info,
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

function authHeaders() {
  const tok = window.localStorage?.getItem('saAccessToken');
  return tok ? { Authorization: `Bearer ${tok}`, 'X-Tenant-Slug': '' } : { 'X-Tenant-Slug': '' };
}

function CopyableCommand({ value }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="flex items-center gap-2 mt-1">
      <code className="flex-1 font-mono text-[12px] bg-zinc-900 text-amber-200 rounded px-3 py-2 overflow-x-auto whitespace-nowrap">
        {value}
      </code>
      <button
        onClick={() => {
          navigator.clipboard?.writeText(value).then(() => {
            setCopied(true); setTimeout(() => setCopied(false), 1500);
          });
        }}
        className="px-2 py-2 border rounded hover:bg-zinc-100"
        title="Copy"
      >
        {copied ? <Check className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4 text-zinc-600" />}
      </button>
    </div>
  );
}

export default function PlatformSettingsPage() {
  const [settings, setSettings] = useState(null);
  const [domain, setDomain] = useState('');
  const [scheme, setScheme] = useState('https');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [dnsStatus, setDnsStatus] = useState(null); // 'ok' | 'fail' | null

  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/saas/settings`, { headers: authHeaders(), withCredentials: true });
      setSettings(data);
      setDomain(data.app_base_domain || '');
      setScheme(data.scheme || 'https');
    } catch (_) {}
    setLoading(false);
  };
  useEffect(() => { refresh(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await axios.patch(
        `${API}/api/saas/settings`,
        { app_base_domain: domain.trim().toLowerCase().replace(/^\./, ''), scheme },
        { headers: authHeaders(), withCredentials: true }
      );
      setSettings(data);
      toast.success('Platform settings saved');
      setDnsStatus(null);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Failed to save');
    }
    setSaving(false);
  };

  const verify = async () => {
    if (!settings?.app_base_domain) return;
    setVerifying(true);
    setDnsStatus(null);
    try {
      // Resolve a known-existing tenant via its expected subdomain. If DNS
      // is wired correctly, this CORS preflight reaches the API. If not,
      // the browser fails and we mark fail.
      const testSlug = 'platform-dns-verify-' + Math.random().toString(36).slice(2, 8);
      const url = `${settings.scheme}://${testSlug}.${settings.app_base_domain}/api/tenant/list`;
      const res = await fetch(url, { method: 'GET', mode: 'cors' });
      setDnsStatus(res.ok || res.status === 400 ? 'ok' : 'fail');
    } catch (_) {
      setDnsStatus('fail');
    }
    setVerifying(false);
  };

  if (loading) return <div className="p-8 text-sm text-zinc-500">Loading settings…</div>;

  const configured = !!settings?.app_base_domain;
  const exampleSubdomain = configured
    ? `acme-clinic.${settings.app_base_domain}`
    : 'acme-clinic.<your-domain>';

  return (
    <div className="p-8 max-w-4xl">
      <h1
        className="text-2xl font-bold text-zinc-900"
        style={{ fontFamily: 'Manrope' }}
        data-testid="sa-settings-title"
      >
        Platform settings
      </h1>
      <p className="text-sm text-zinc-500 mt-1">
        Wire up wildcard DNS once. After that, every tenant gets a clean
        subdomain URL instead of <span className="font-mono">?tenant=…</span>.
      </p>

      <div className="mt-6 bg-white border rounded-xl p-5">
        <div className="flex items-center gap-2 mb-1">
          <Globe className="h-4 w-4 text-amber-600" />
          <h3 className="font-semibold text-zinc-900">Base domain</h3>
        </div>
        <p className="text-xs text-zinc-500 mb-4">
          The bare hostname your app is served from (no scheme, no path).
          Tenants will live at <span className="font-mono">&lt;slug&gt;.&lt;this-domain&gt;</span>.
        </p>
        <div className="grid grid-cols-3 gap-3">
          <div className="col-span-2 space-y-1.5">
            <Label htmlFor="ps-domain">App base domain</Label>
            <Input id="ps-domain" value={domain}
              onChange={(e) => setDomain(e.target.value)}
              placeholder="ecare360.app"
              data-testid="sa-settings-domain" />
            {settings?.is_env_override && (
              <p className="text-[11px] text-amber-700 flex items-center gap-1">
                <Info className="h-3 w-3" />
                Inheriting from <span className="font-mono">APP_BASE_DOMAIN</span> env. Saving here will override it in DB.
              </p>
            )}
          </div>
          <div className="col-span-1 space-y-1.5">
            <Label htmlFor="ps-scheme">Scheme</Label>
            <select
              id="ps-scheme"
              value={scheme}
              onChange={(e) => setScheme(e.target.value)}
              className="h-10 w-full px-3 border rounded-md text-sm bg-white"
              data-testid="sa-settings-scheme"
            >
              <option value="https">https (recommended)</option>
              <option value="http">http (local dev)</option>
            </select>
          </div>
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <Button onClick={save} disabled={saving}
            className="bg-amber-600 hover:bg-amber-700"
            data-testid="sa-settings-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
            Save
          </Button>
          {configured && (
            <Button onClick={verify} disabled={verifying} variant="outline"
              data-testid="sa-settings-verify">
              {verifying ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Verify DNS
            </Button>
          )}
          {dnsStatus === 'ok' && (
            <span className="text-sm text-emerald-700 flex items-center gap-1" data-testid="sa-settings-dns-ok">
              <CheckCircle2 className="h-4 w-4" /> Wildcard DNS resolves
            </span>
          )}
          {dnsStatus === 'fail' && (
            <span className="text-sm text-red-700 flex items-center gap-1" data-testid="sa-settings-dns-fail">
              <AlertCircle className="h-4 w-4" /> DNS not reachable — check your wildcard record
            </span>
          )}
        </div>
      </div>

      <div className="mt-6 bg-white border rounded-xl p-5">
        <div className="flex items-center gap-2 mb-3">
          <Server className="h-4 w-4 text-zinc-600" />
          <h3 className="font-semibold text-zinc-900">How to wire production DNS</h3>
        </div>
        <ol className="space-y-4 text-sm text-zinc-700">
          <li>
            <span className="font-medium text-zinc-900">1. Point a wildcard <code className="bg-zinc-100 rounded px-1 py-0.5 text-[12px]">A</code> or <code className="bg-zinc-100 rounded px-1 py-0.5 text-[12px]">CNAME</code> record</span>
            <p className="text-zinc-500 mt-1 text-xs">
              At your DNS provider (Cloudflare, Route 53, etc.), create:
            </p>
            <CopyableCommand
              value={`*.${settings?.app_base_domain || 'your-domain.com'}   CNAME   app.${settings?.app_base_domain || 'your-domain.com'}`}
            />
          </li>
          <li>
            <span className="font-medium text-zinc-900">2. Issue a wildcard TLS certificate</span>
            <p className="text-zinc-500 mt-1 text-xs">
              Use Let's Encrypt / Caddy / AWS ACM to cover the wildcard. With Caddy:
            </p>
            <CopyableCommand
              value={`*.${settings?.app_base_domain || 'your-domain.com'} { reverse_proxy app-backend:8001 }`}
            />
          </li>
          <li>
            <span className="font-medium text-zinc-900">3. Save the base domain above</span>
            <p className="text-zinc-500 mt-1 text-xs">
              The backend will start rendering subdomain URLs immediately.
              Existing tenants don't need any migration — their slugs become subdomains.
            </p>
          </li>
          <li>
            <span className="font-medium text-zinc-900">4. (Optional) Verify with the button above</span>
            <p className="text-zinc-500 mt-1 text-xs">
              The verifier hits a fresh random subdomain to confirm your wildcard catches it.
            </p>
          </li>
        </ol>
      </div>

      <div className="mt-6 bg-blue-50 border border-blue-200 rounded-xl p-5">
        <h3 className="font-semibold text-blue-900 flex items-center gap-2">
          <ExternalLink className="h-4 w-4" /> Preview the result
        </h3>
        <p className="text-sm text-blue-900/80 mt-1">
          A tenant with slug <span className="font-mono">acme-clinic</span> will sign in at:
        </p>
        <CopyableCommand
          value={`${scheme}://${exampleSubdomain}/login`}
        />
        {!configured && (
          <p className="text-[12px] text-blue-900/70 mt-2">
            (Until you set a base domain, tenant URLs use <span className="font-mono">https://&lt;your-host&gt;/login?tenant=&lt;slug&gt;</span> which still works but isn't pretty.)
          </p>
        )}
      </div>
    </div>
  );
}
